import { useEffect, useState } from "react";
import { authFetchJson } from "@/lib/api";
import { AdminUserDrawer } from "./AdminUserDrawer";
import { LoginsSection } from "./telemetry/LoginsSection";
import { ProblemsSection } from "./telemetry/ProblemsSection";
import { ScreenTimeSection } from "./telemetry/ScreenTimeSection";
import { VersionsSection } from "./telemetry/VersionsSection";
import type { TelemetryOverview } from "./telemetry/types";
import { ErrorBanner, PeriodToggle, StatCard } from "./telemetry/ui";

export function AdminTelemetryTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<TelemetryOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drawerUser, setDrawerUser] = useState<{ id: string; email: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetchJson<TelemetryOverview>(`/api/admin/telemetry/overview?days=${String(days)}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const openUser = (id: string, email: string) => { setDrawerUser({ id, email }); };

  return (
    <div className="space-y-8 p-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="cs-h1 text-[var(--cs-navy)]">Telemetrie</h1>
          <p className="text-sm text-[var(--cs-ink-muted)]">
            Schermtijd, problemen, uploads, aanmeldingen en versies van widget en mobiele app
          </p>
        </div>
        <PeriodToggle days={days} onChange={setDays} />
      </header>

      {error ? <ErrorBanner message={`Telemetrie tijdelijk niet beschikbaar: ${error}`} /> : null}

      <section className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <StatCard label="Actief · widget" value={data?.active_users.widget ?? 0} loading={loading} />
        <StatCard label="Actief · mobiel" value={data?.active_users.mobile ?? 0} loading={loading} />
        <StatCard label="Sessies" value={data?.sessions ?? 0} loading={loading} />
        <StatCard label="Fouten" value={data?.errors.total ?? 0} loading={loading} />
        <StatCard
          label="Mislukte uploads"
          value={data?.uploads.failed ?? 0}
          loading={loading}
          hint={data ? `${String(data.uploads.permanent)} definitief` : undefined}
        />
      </section>

      <ScreenTimeSection rows={data?.screen_time ?? []} loading={loading} />
      {data ? (
        <ProblemsSection errors={data.errors} uploads={data.uploads} problemUsers={data.problem_users} days={days} onUserClick={openUser} />
      ) : null}
      <LoginsSection days={days} onUserClick={openUser} />
      <VersionsSection />

      <AdminUserDrawer user={drawerUser} onClose={() => { setDrawerUser(null); }} />
    </div>
  );
}
